import Phaser from "phaser";
import { useEffect, useRef, useState } from "react";

export default function ResumeKingdom() {
  const gameRef = useRef(null);
  const containerRef = useRef(null);
  const [checkpoint, setCheckpoint] = useState("intro");
  const [hint, setHint] = useState(
    "Tap left/right to move, middle to jump, or scroll/swipe to explore"
  );
  const [isLoading, setIsLoading] = useState(true);
  const [level, setLevel] = useState(1);

  // Keep a single Phaser instance
  useEffect(() => {
    if (gameRef.current || !containerRef.current) return;

    const WIDTH = containerRef.current.clientWidth || 1024;
    const HEIGHT = Math.max(600, Math.min(window.innerHeight - 80, 760));

    // Event bus to talk Scene -> React
    const bridge = new Phaser.Events.EventEmitter();
    bridge.on("checkpoint", (key) => setCheckpoint(key));
    bridge.on("hint", (msg) => setHint(msg));
    bridge.on("level", (lvl) => setLevel(lvl));
    bridge.on("loaded", () => setIsLoading(false));

    // --- Enhanced Phaser Scene ---
    class KingdomScene extends Phaser.Scene {
      constructor() {
        super("KingdomScene");
      }

      init() {
        this.worldWidth = 7500; // long side-scroll world
        this.worldHeight = 4000;
        this.groundY = HEIGHT - 80;
        this.isMoving = false;
        this.facingRight = true;
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.isScrolling = false;

        // Mobile touch controls
        this.isTouchingLeft = false;
        this.isTouchingRight = false;
        this.touchMoveThreshold = 10;
      }

      preload() {
        // Load images with fallback handling
        this.load.on("loaderror", (file) => {
          console.log(`${file.key} not found, creating fallback texture`);
          this.createFallbackTexture(file.key);
        });

        this.load.svg("king", "/assets/king.svg", { scale: 0.25 });
        this.load.svg("vijay", "/assets/vijay.svg", { scale: 0.5 });
        this.load.image("about", "/assets/about.png");
        this.load.image("skill", "/assets/skill.png");
        this.load.image("project", "/assets/project.png");
        this.load.image("education", "/assets/education.png");
        this.load.image("experience", "/assets/experience.png");

        // Create simple textures for other game elements
        this.createGameTextures();
      }

      createFallbackTexture(key) {
        const g = this.make.graphics({ x: 0, y: 0, add: false });

        switch (key) {
          case "king":
            this.createFallbackKing(g);
            break;
          case "vijay":
            this.createFallbackVijay(g);
            break;
          case "about":
            this.createFallbackAbout(g);
            break;
          case "skill":
            this.createFallbackSkill(g);
            break;
          case "project":
            this.createFallbackProject(g);
            break;
          case "experience":
            this.createFallbackExperience(g);
            break;
          case "education":
            this.createFallbackEducation(g);
            break;
          default:
            break;
        }
      }

      createFallbackKing(g) {
        // Fallback king character (properly facing right by default)
        g.fillStyle(0xfde68a, 1); // body
        g.fillRoundedRect(8, 16, 32, 28, 4);

        g.fillStyle(0xfcd34d, 1); // crown
        g.fillTriangle(12, 16, 24, 4, 36, 16);

        g.fillStyle(0x374151, 1); // eyes
        g.fillCircle(20, 20, 2);
        g.fillCircle(28, 20, 2);

        g.fillStyle(0x7c2d12, 1); // cape
        g.fillRoundedRect(2, 24, 8, 20, 4);

        g.generateTexture("king", 48, 48);
        g.clear();
      }

      createFallbackVijay(g) {
        // Simple tower/pillar structure
        g.fillStyle(0xd97706, 1);
        g.fillRect(20, 50, 40, 200);

        g.fillStyle(0xb45309, 1);
        g.fillRect(15, 240, 50, 20);
        g.fillRect(10, 40, 60, 20);

        g.generateTexture("vijay", 80, 260);
        g.clear();
      }

      createFallbackAbout(g) {
        // Simple portrait placeholder
        g.fillStyle(0x374151, 1);
        g.fillRoundedRect(10, 10, 80, 100, 10);

        g.fillStyle(0xf3f4f6, 1);
        g.fillText("ABOUT", 25, 55, { fontSize: "12px" });

        g.generateTexture("about", 100, 120);
        g.clear();
      }

      createFallbackSkill(g) {
        g.fillGradientStyle(0x1e3a8a, 0x1e3a8a, 0x1e40af, 0x1e40af, 1);
        g.fillRect(0, 0, 400, 300);

        g.fillStyle(0x60a5fa, 1);
        g.fillCircle(50, 50, 20);
        g.fillCircle(150, 75, 15);
        g.fillCircle(250, 60, 22);
        g.fillCircle(100, 150, 18);
        g.fillCircle(300, 120, 20);

        g.lineStyle(2, 0x3b82f6, 0.6);
        g.lineBetween(50, 50, 150, 75);
        g.lineBetween(150, 75, 250, 60);
        g.lineBetween(100, 150, 150, 75);
        g.lineBetween(250, 60, 300, 120);

        g.generateTexture("skill", 400, 300);
        g.clear();
      }

      createFallbackProject(g) {
        g.fillGradientStyle(0x059669, 0x059669, 0x047857, 0x047857, 1);
        g.fillRect(0, 0, 400, 300);

        g.fillStyle(0x1f2937, 1);
        g.fillRoundedRect(20, 20, 150, 100, 8);
        g.fillRoundedRect(190, 40, 150, 100, 8);
        g.fillRoundedRect(50, 160, 150, 100, 8);

        g.fillStyle(0x374151, 1);
        g.fillRoundedRect(20, 20, 150, 20, 8);
        g.fillRoundedRect(190, 40, 150, 20, 8);
        g.fillRoundedRect(50, 160, 150, 20, 8);

        g.lineStyle(2, 0x10b981);
        for (let i = 0; i < 15; i++) {
          const y = 45 + (i % 5) * 15;
          const x = 25 + Math.floor(i / 5) * 120;
          const width = 30 + Math.random() * 80;
          g.lineBetween(x, y, x + width, y);
        }

        g.generateTexture("project", 400, 300);
        g.clear();
      }

      createFallbackExperience(g) {
        g.fillGradientStyle(0x7c2d12, 0x7c2d12, 0x92400e, 0x92400e, 1);
        g.fillRect(0, 0, 400, 300);

        g.fillStyle(0xdc2626, 1);
        g.fillRoundedRect(50, 200, 80, 15, 3);
        g.fillStyle(0x2563eb, 1);
        g.fillRoundedRect(55, 185, 80, 15, 3);
        g.fillStyle(0x059669, 1);
        g.fillRoundedRect(60, 170, 80, 15, 3);

        g.fillStyle(0x1f2937, 1);
        g.fillRect(200, 100, 100, 8);
        g.fillCircle(250, 90, 25);

        g.lineStyle(3, 0xfcd34d);
        g.lineBetween(270, 85, 285, 70);
        g.fillStyle(0xfcd34d, 1);
        g.fillCircle(285, 70, 4);

        g.fillStyle(0xf3f4f6, 1);
        g.fillRoundedRect(250, 150, 120, 90, 5);
        g.lineStyle(2, 0xd97706);
        g.strokeRoundedRect(250, 150, 120, 90, 5);

        g.generateTexture("experience", 400, 300);
        g.clear();
      }

      createFallbackEducation(g) {
        g.fillGradientStyle(0x7c2d12, 0x7c2d12, 0x92400e, 0x92400e, 1);
        g.fillRect(0, 0, 400, 300);

        g.fillStyle(0xdc2626, 1);
        g.fillRoundedRect(50, 200, 80, 15, 3);
        g.fillStyle(0x2563eb, 1);
        g.fillRoundedRect(55, 185, 80, 15, 3);
        g.fillStyle(0x059669, 1);
        g.fillRoundedRect(60, 170, 80, 15, 3);

        g.fillStyle(0x1f2937, 1);
        g.fillRect(200, 100, 100, 8);
        g.fillCircle(250, 90, 25);

        g.lineStyle(3, 0xfcd34d);
        g.lineBetween(270, 85, 285, 70);
        g.fillStyle(0xfcd34d, 1);
        g.fillCircle(285, 70, 4);

        g.fillStyle(0xf3f4f6, 1);
        g.fillRoundedRect(250, 150, 120, 90, 5);
        g.lineStyle(2, 0xd97706);
        g.strokeRoundedRect(250, 150, 120, 90, 5);

        g.generateTexture("education", 400, 300);
        g.clear();
      }

      createGameTextures() {
        const g = this.make.graphics({ x: 0, y: 0, add: false });

        // Ground tile
        g.fillGradientStyle(0x4b5563, 0x4b5563, 0x374151, 0x374151, 1);
        g.fillRect(0, 0, 64, 32);
        g.lineStyle(1, 0x6b7280, 0.5);
        g.strokeRect(0, 0, 64, 32);
        g.generateTexture("ground", 64, 32);
        g.clear();

        // Skill token
        g.fillGradientStyle(0xfcd34d, 0xfcd34d, 0xf59e0b, 0xf59e0b, 1);
        g.fillCircle(10, 10, 8);
        g.lineStyle(2, 0xfde68a, 1);
        g.strokeCircle(10, 10, 8);
        g.fillStyle(0xdc2626, 1);
        g.fillCircle(10, 10, 4);
        g.generateTexture("token", 20, 20);
        g.clear();

        // Cloud
        g.fillStyle(0xffffff, 0.8);
        g.fillCircle(20, 15, 12);
        g.fillCircle(35, 15, 15);
        g.fillCircle(50, 15, 12);
        g.fillCircle(30, 10, 8);
        g.generateTexture("cloud", 70, 30);
        g.clear();

        // Sun
        g.fillStyle(0xfde68a, 1);
        g.fillCircle(25, 25, 20);
        g.lineStyle(3, 0xf59e0b);
        for (let i = 0; i < 8; i++) {
          const angle = (i / 8) * Math.PI * 2;
          const x1 = 25 + Math.cos(angle) * 25;
          const y1 = 25 + Math.sin(angle) * 25;
          const x2 = 25 + Math.cos(angle) * 35;
          const y2 = 25 + Math.sin(angle) * 35;
          g.lineBetween(x1, y1, x2, y2);
        }
        g.generateTexture("sun", 70, 70);
        g.clear();
      }

      create() {
        // Physics world
        this.physics.world.setBounds(0, 0, this.worldWidth, HEIGHT);

        this.createSkyBackground();
        this.createBackgroundText();
        this.createGroundPlatforms();

        // Player
        this.createPlayer();

        // Colliders
        this.physics.add.collider(this.player, this.platforms);

        // Camera setup
        this.cameras.main.setBounds(0, 0, this.worldWidth, HEIGHT);
        this.cameras.main.startFollow(this.player, true, 0.12, 0.12);
        this.cameras.main.setLerp(0.15, 0.15);

        this.cursors = this.input.keyboard.createCursorKeys();
        this.wasd = this.input.keyboard.addKeys("W,S,A,D");

        // Input handling
        this.setupMouseAndTouchControls();

        // Sections
        this.createCheckpoints();
        this.createAboutMeSection();
        this.createSkillsSection();
        this.createProjectsSection();
        this.createExperienceSection();
        this.createEducationSection();
        this.createHireMeSection();
        this.createDecorations();

        bridge.emit("checkpoint", "intro");
        bridge.emit("level", 1);
        bridge.emit("loaded");
      }

      setupMouseAndTouchControls() {
        // Mouse wheel scrolling
        this.input.on("wheel", (pointer, gameObjects, deltaX, deltaY) => {
          const scrollSensitivity = 3;
          const targetX =
            this.cameras.main.scrollX + deltaY * scrollSensitivity;
          const clampedX = Phaser.Math.Clamp(
            targetX,
            0,
            this.worldWidth - WIDTH
          );

          this.cameras.main.scrollX = clampedX;

          // Move player to keep in sync with camera
          const playerTargetX = clampedX + WIDTH / 2;
          this.player.x = Phaser.Math.Clamp(
            playerTargetX,
            60,
            this.worldWidth - 60
          );

          this.updateCheckpointFromPosition(this.player.x);
        });

        // Touch controls (left/right/jump + scroll)
        this.input.on("pointerdown", (pointer) => {
          this.touchStartX = pointer.x;
          this.touchStartY = pointer.y;
          this.isScrolling = false;

          const leftZone = WIDTH * 0.3;
          const rightZone = WIDTH * 0.7;

          if (pointer.x < leftZone) {
            this.isTouchingLeft = true;
            this.isTouchingRight = false;
          } else if (pointer.x > rightZone) {
            this.isTouchingRight = true;
            this.isTouchingLeft = false;
          } else {
            // Middle zone - jump
            if (this.player.body.onFloor()) {
              this.player.setVelocityY(-420);
              this.tweens.add({
                targets: this.player,
                scaleY: 1.2,
                scaleX: 0.8,
                duration: 100,
                yoyo: true,
                ease: "power2.out",
              });
            }
          }
        });

        this.input.on("pointermove", (pointer) => {
          if (pointer.isDown) {
            const deltaX = this.touchStartX - pointer.x;
            const deltaY = Math.abs(this.touchStartY - pointer.y);

            if (
              Math.abs(deltaX) > this.touchMoveThreshold &&
              Math.abs(deltaX) > deltaY
            ) {
              this.isScrolling = true;
              this.isTouchingLeft = false;
              this.isTouchingRight = false;

              const scrollSensitivity = 2;
              const targetX =
                this.cameras.main.scrollX + deltaX * scrollSensitivity;
              const clampedX = Phaser.Math.Clamp(
                targetX,
                0,
                this.worldWidth - WIDTH
              );

              this.cameras.main.scrollX = clampedX;

              const playerTargetX = clampedX + WIDTH / 2;
              this.player.x = Phaser.Math.Clamp(
                playerTargetX,
                60,
                this.worldWidth - 60
              );

              this.touchStartX = pointer.x;
              this.updateCheckpointFromPosition(this.player.x);
            }
          }
        });

        this.input.on("pointerup", () => {
          this.isTouchingLeft = false;
          this.isTouchingRight = false;
          this.isScrolling = false;
        });

        this.input.on("pointerout", () => {
          this.isTouchingLeft = false;
          this.isTouchingRight = false;
        });
      }

      updateCheckpointFromPosition(x) {
        let newCheckpoint = "intro";
        let newLevel = 1;

        if (x >= 6400) {
          newCheckpoint = "hire";
          newLevel = 7;
        } else if (x >= 5000) {
          newCheckpoint = "education";
          newLevel = 6;
        } else if (x >= 4200) {
          newCheckpoint = "experience";
          newLevel = 5;
        } else if (x >= 3400) {
          newCheckpoint = "projects";
          newLevel = 4;
        } else if (x >= 2500) {
          newCheckpoint = "skills";
          newLevel = 3;
        } else if (x >= 1500) {
          newCheckpoint = "about";
          newLevel = 2;
        }

        if (this._lastCheckpoint !== newCheckpoint) {
          this._lastCheckpoint = newCheckpoint;
          bridge.emit("checkpoint", newCheckpoint);
          bridge.emit("level", newLevel);
        }
      }

      createGroundPlatforms() {
        this.platforms = this.physics.add.staticGroup();

        // Regular ground for entire world
        for (let x = 0; x < this.worldWidth; x += 64) {
          this.platforms
            .create(x, this.groundY, "ground")
            .setOrigin(0, 0.5)
            .refreshBody();
        }
      }

      createSkyBackground() {
        const sky1 = this.add
          .rectangle(0, 0, this.worldWidth, HEIGHT * 0.6, 0x87ceeb)
          .setOrigin(0, 0);
        const sky2 = this.add
          .rectangle(0, HEIGHT * 0.3, this.worldWidth, HEIGHT * 0.4, 0xb0e0e6)
          .setOrigin(0, 0);

        sky1.setScrollFactor(0.1);
        sky2.setScrollFactor(0.2);

        const sun = this.add.image(200, 120, "sun").setScrollFactor(0.1);
        this.tweens.add({
          targets: sun,
          rotation: Math.PI * 2,
          duration: 20000,
          repeat: -1,
          ease: "none",
        });

        for (let i = 0; i < 8; i++) {
          const cloud = this.add
            .image(
              Math.random() * this.worldWidth,
              50 + Math.random() * 150,
              "cloud"
            )
            .setScrollFactor(0.3 + Math.random() * 0.2);

          this.tweens.add({
            targets: cloud,
            x: cloud.x + 200,
            duration: 15000 + Math.random() * 10000,
            repeat: -1,
            ease: "none",
            yoyo: true,
          });
        }
      }

      createBackgroundText() {
        // Intro big text
        this.add
          .text(500, HEIGHT / 2, "Hi, I am\nRUDRAKSH LADDHA", {
            fontSize: "48px",
            fontFamily: "serif",
            color: "#f7921e",
            align: "center",
            fontStyle: "bold",
            stroke: "#000000",
            strokeThickness: 8,
          })
          .setOrigin(0.5)
          .setScrollFactor(0.8)
          .setAlpha(0.3);

        const sections = [{ x: 6500, text: "HIRE ME" }];

        sections.forEach(({ x, text }) => {
          this.add
            .text(x, HEIGHT / 2 - 100, text, {
              fontSize: "36px",
              fontFamily: "serif",
              color: "#f5bc42",
              align: "center",
              fontStyle: "bold",
              stroke: "#92400E",
              strokeThickness: 2,
            })
            .setOrigin(0.5)
            .setScrollFactor(0.9)
            .setAlpha(0.4);
        });
      }

      createPlayer() {
        this.player = this.physics.add.sprite(
          120,
          this.groundY - 60,
          "king"
        );
        this.player.setCollideWorldBounds(true);
        this.player.setBounce(0.1);
        this.player.body.setSize(32, 40).setOffset(8, 8);

        // Idle breathing
        this.tweens.add({
          targets: this.player,
          scaleY: 0.98,
          duration: 1500,
          yoyo: true,
          repeat: -1,
          ease: "sine.inOut",
        });
      }

      createCheckpoints() {
        const checkpoints = [
          { x: 600, key: "intro", level: 1 },
          { x: 1600, key: "about", level: 2 },
          { x: 2600, key: "skills", level: 3 },
          { x: 3500, key: "projects", level: 4 },
          { x: 4400, key: "experience", level: 5 },
          { x: 5200, key: "education", level: 6 },
          { x: 6500, key: "hire", level: 7 },
        ];

        checkpoints.forEach(({ x, key, level }) => {
          const zone = this.add
            .zone(x, this.groundY - 60, 120, 160)
            .setOrigin(0.5);
          this.physics.world.enable(zone);
          zone.body.setAllowGravity(false);
          zone.body.moves = false;

          this.physics.add.overlap(this.player, zone, () => {
            if (this._lastCheckpoint !== key) {
              this._lastCheckpoint = key;
              this.time.delayedCall(50, () => {
                bridge.emit("checkpoint", key);
                bridge.emit("level", level);
              });
            }
          });
        });
      }

      createAboutMeSection() {
        const chittorgarhVijay = this.add
          .image(1650, this.groundY - 200, "vijay")
          .setScrollFactor(0.6)
          .setScale(0.6)
          .setAlpha(0.8);

        this.add
          .image(1600, this.groundY - 180, "about")
          .setScale(0.4)
          .setAlpha(0.9)
          .setScrollFactor(0.8);

        this.tweens.add({
          targets: chittorgarhVijay,
          scaleY: 0.65,
          duration: 5000,
          yoyo: true,
          repeat: -1,
          ease: "sine.inOut",
        });
      }

      createSkillsSection() {
        this.add
          .image(2600, this.groundY - 200, "skill")
          .setScale(0.4)
          .setAlpha(0.9)
          .setScrollFactor(0.8);

        this.skillTokens = this.physics.add.group();
        const skillTokenPositions = [2600, 2700, 2800, 2900, 3000];

        skillTokenPositions.forEach((x, index) => {
          const token = this.skillTokens.create(
            x,
            this.groundY - 80 - (index % 3) * 30,
            "token"
          );
          token.setBounce(0.4);
          token.body.setAllowGravity(false);

          this.tweens.add({
            targets: token,
            y: token.y - 20,
            duration: 1800 + index * 300,
            yoyo: true,
            repeat: -1,
            ease: "sine.inOut",
          });

          this.tweens.add({
            targets: token,
            rotation: Math.PI * 2,
            duration: 4000 + index * 500,
            repeat: -1,
            ease: "none",
          });
        });

        this.physics.add.overlap(
          this.player,
          this.skillTokens,
          (player, token) => {
            this.tweens.add({
              targets: token,
              scale: 2,
              alpha: 0,
              rotation: token.rotation + Math.PI,
              duration: 400,
              ease: "back.out",
              onComplete: () => token.destroy(),
            });

            this.createSkillParticles(token.x, token.y);
            bridge.emit("hint", "⭐ Skill mastered! Keep collecting!");
          }
        );
      }

      createProjectsSection() {
        this.add
          .image(3400, this.groundY - 200, "project")
          .setScale(0.4)
          .setAlpha(0.9)
          .setScrollFactor(0.8);

        this.projectTokens = this.physics.add.group();
        const projectTokenPositions = [3600, 3720, 3840, 3960];

        projectTokenPositions.forEach((x, index) => {
          const token = this.projectTokens.create(
            x,
            this.groundY - 100 - (index % 2) * 40,
            "token"
          );
          token.setBounce(0.3);
          token.body.setAllowGravity(false);
          token.setTint(0x10b981);

          this.tweens.add({
            targets: token,
            y: token.y - 25,
            duration: 2200 + index * 400,
            yoyo: true,
            repeat: -1,
            ease: "sine.inOut",
          });

          this.tweens.add({
            targets: token,
            rotation: Math.PI * 2,
            duration: 6000 + index * 800,
            repeat: -1,
            ease: "none",
          });
        });

        this.physics.add.overlap(
          this.player,
          this.projectTokens,
          (player, token) => {
            this.tweens.add({
              targets: token,
              scale: 2.2,
              alpha: 0,
              rotation: token.rotation + Math.PI * 1.5,
              duration: 500,
              ease: "back.out",
              onComplete: () => token.destroy(),
            });

            this.createProjectParticles(token.x, token.y);
            bridge.emit("hint", "🚀 Project conquered! Innovation achieved!");
          }
        );
      }

      // EXPERIENCE SECTION (before gap)
      createExperienceSection() {
        this.add
          .image(4200, this.groundY - 190, "experience")
          .setScale(0.5)
          .setAlpha(0.9)
          .setScrollFactor(0.8);

        this.experienceTokens = this.physics.add.group();
        const expPositions = [4300, 4450, 4550, 4650];

        expPositions.forEach((x, index) => {
          const token = this.experienceTokens.create(
            x,
            this.groundY - 90 - (index % 2) * 35,
            "token"
          );
          token.setBounce(0.2);
          token.body.setAllowGravity(false);
          token.setTint(0x7c2d12);

          this.tweens.add({
            targets: token,
            y: token.y - 18,
            duration: 2500 + index * 350,
            yoyo: true,
            repeat: -1,
            ease: "sine.inOut",
          });

          this.tweens.add({
            targets: token,
            rotation: Math.PI * 2,
            duration: 8000 + index * 1000,
            repeat: -1,
            ease: "none",
          });
        });

        this.physics.add.overlap(
          this.player,
          this.experienceTokens,
          (player, token) => {
            this.tweens.add({
              targets: token,
              scale: 2.5,
              alpha: 0,
              rotation: token.rotation + Math.PI * 2,
              duration: 600,
              ease: "back.out",
              onComplete: () => token.destroy(),
            });

            this.createExperienceParticles(token.x, token.y);
            bridge.emit("hint", "💼 Experience gained! Wisdom sharpened!");
          }
        );
      }

      // EDUCATION SECTION (after a gap)
      createEducationSection() {
        this.add
          .image(5100, this.groundY - 190, "education")
          .setScale(0.5)
          .setAlpha(0.9)
          .setScrollFactor(0.8);

        this.educationTokens = this.physics.add.group();
        const eduPositions = [5200, 5350, 5450, 5550];

        eduPositions.forEach((x, index) => {
          const token = this.educationTokens.create(
            x,
            this.groundY - 90 - (index % 3) * 35,
            "token"
          );
          token.setBounce(0.2);
          token.body.setAllowGravity(false);
          token.setTint(0x92400e);

          this.tweens.add({
            targets: token,
            y: token.y - 18,
            duration: 2600 + index * 350,
            yoyo: true,
            repeat: -1,
            ease: "sine.inOut",
          });

          this.tweens.add({
            targets: token,
            rotation: Math.PI * 2,
            duration: 8000 + index * 1000,
            repeat: -1,
            ease: "none",
          });
        });

        this.physics.add.overlap(
          this.player,
          this.educationTokens,
          (player, token) => {
            this.tweens.add({
              targets: token,
              scale: 2.5,
              alpha: 0,
              rotation: token.rotation + Math.PI * 2,
              duration: 600,
              ease: "back.out",
              onComplete: () => token.destroy(),
            });

            this.createEducationParticles(token.x, token.y);
            bridge.emit("hint", "📚 Knowledge gained! Wisdom increased!");
          }
        );
      }

    createHireMeSection() {
  const buttonData = [
    {
      text: "🌐 PORTFOLIO",
      url: "https://rudrakshladdha.xyz/",
      x: 7200,
      y: this.groundY - 140,
      color: 0x3b82f6,
      hoverColor: 0x1d4ed8,
    },
    {
      text: "💼 LINKEDIN",
      url: "https://www.linkedin.com/in/rudraksh-laddha2/",
      x: 7000,
      y: this.groundY - 140,
      color: 0x0077b5,
      hoverColor: 0x005582,
    },
    {
      text: "🐦 TWITTER",
      url: "https://x.com/Rudraksh_Laddha",
      x: 7000,
      y: this.groundY - 220,
      color: 0x1da1f2,
      hoverColor: 0x0d8bd9,
    },
    {
      text: "📅 BOOK CALL",
      url: "https://cal.com/rudrksh-laddha",
      x: 7200,
      y: this.groundY - 220,
      color: 0xf59e0b,
      hoverColor: 0xd97706,
    }
  ];

  this.hireMeButtons = [];

  buttonData.forEach((btn) => {
    const bg = this.add.graphics()
      .fillStyle(btn.color, 0.9)
      .fillRoundedRect(-60, -20, 120, 40, 8)
      .lineStyle(2, 0xffffff, 0.8)
      .strokeRoundedRect(-60, -20, 120, 40, 8);

    const text = this.add.text(0, 0, btn.text, {
      fontSize: "12px",
      color: "#fff",
      fontWeight: "bold",
    }).setOrigin(0.5);

    const container = this.add.container(btn.x, btn.y, [bg, text]);
    container.setSize(120, 40);
    container.setInteractive();

    container.on("pointerdown", () => window.open(btn.url, "_blank"));

    this.tweens.add({
      targets: container,
      y: btn.y - 10,
      duration: 2000 + Math.random() * 1000,
      yoyo: true,
      repeat: -1,
      ease: "sine.inOut",
    });
  });
}


      createSkillParticles(x, y) {
        for (let i = 0; i < 6; i++) {
          const particle = this.add.circle(x, y, 4, 0xfcd34d, 0.8);
          this.tweens.add({
            targets: particle,
            x: x + (Math.random() - 0.5) * 80,
            y: y - Math.random() * 60,
            alpha: 0,
            scale: 0.2,
            duration: 1000,
            ease: "power2.out",
            onComplete: () => particle.destroy(),
          });
        }
      }

      createProjectParticles(x, y) {
        for (let i = 0; i < 8; i++) {
          const particle = this.add.circle(x, y, 5, 0x10b981, 0.9);
          this.tweens.add({
            targets: particle,
            x: x + (Math.random() - 0.5) * 100,
            y: y - Math.random() * 80,
            alpha: 0,
            scale: 0.1,
            duration: 1200,
            ease: "power2.out",
            onComplete: () => particle.destroy(),
          });
        }
      }

      createExperienceParticles(x, y) {
        for (let i = 0; i < 7; i++) {
          const particle = this.add.circle(x, y, 4, 0x7c2d12, 0.8);
          this.tweens.add({
            targets: particle,
            x: x + (Math.random() - 0.5) * 90,
            y: y - Math.random() * 70,
            alpha: 0,
            scale: 0.15,
            duration: 1100,
            ease: "power2.out",
            onComplete: () => particle.destroy(),
          });
        }
      }

      createEducationParticles(x, y) {
        for (let i = 0; i < 7; i++) {
          const particle = this.add.circle(x, y, 4, 0x92400e, 0.8);
          this.tweens.add({
            targets: particle,
            x: x + (Math.random() - 0.5) * 90,
            y: y - Math.random() * 70,
            alpha: 0,
            scale: 0.15,
            duration: 1100,
            ease: "power2.out",
            onComplete: () => particle.destroy(),
          });
        }
      }

      createDecorations() {
        this.regularTokens = this.physics.add.group();
        const regularTokenPositions = [800, 1200, 4600, 4900];

        regularTokenPositions.forEach((x, index) => {
          const token = this.regularTokens.create(
            x,
            this.groundY - 80,
            "token"
          );
          token.setBounce(0.4);
          token.body.setAllowGravity(false);

          this.tweens.add({
            targets: token,
            y: token.y - 15,
            duration: 1500 + index * 200,
            yoyo: true,
            repeat: -1,
            ease: "sine.inOut",
          });
        });

        this.physics.add.overlap(
          this.player,
          this.regularTokens,
          (player, token) => {
            this.tweens.add({
              targets: token,
              scale: 1.5,
              alpha: 0,
              duration: 200,
              ease: "back.out",
              onComplete: () => token.destroy(),
            });

            bridge.emit("hint", "⭐ Token collected!");
          }
        );
      }

      update() {
        const speed = 280;
        const jumpPower = 420;
        const onFloor = this.player.body.onFloor();

        let moving = false;

        if (
          this.cursors.left.isDown ||
          this.wasd.A.isDown ||
          this.isTouchingLeft
        ) {
          this.player.setVelocityX(-speed);
          if (this.facingRight) {
            this.player.setFlipX(true);
            this.facingRight = false;
          }
          moving = true;
        } else if (
          this.cursors.right.isDown ||
          this.wasd.D.isDown ||
          this.isTouchingRight
        ) {
          this.player.setVelocityX(speed);
          if (!this.facingRight) {
            this.player.setFlipX(false);
            this.facingRight = true;
          }
          moving = true;
        } else {
          this.player.setVelocityX(0);
        }

        if (
          (Phaser.Input.Keyboard.JustDown(this.cursors.space) ||
            Phaser.Input.Keyboard.JustDown(this.wasd.W)) &&
          onFloor
        ) {
          this.player.setVelocityY(-jumpPower);

          this.tweens.add({
            targets: this.player,
            scaleY: 1.2,
            scaleX: 0.8,
            duration: 100,
            yoyo: true,
            ease: "power2.out",
          });
        }

        if (moving && onFloor && !this.isMoving) {
          this.isMoving = true;
          this.walkTween = this.tweens.add({
            targets: this.player,
            scaleX: 0.95,
            scaleY: 1.05,
            duration: 150,
            yoyo: true,
            repeat: -1,
            ease: "sine.inOut",
          });
        } else if (!moving && this.isMoving) {
          this.isMoving = false;
          if (this.walkTween) {
            this.walkTween.stop();
            this.tweens.add({
              targets: this.player,
              scaleX: 1,
              scaleY: 1,
              duration: 200,
              ease: "back.out",
            });
          }
        }

        this.updateContextualHints();
      }

      updateContextualHints() {
        const playerX = this.player.x;

        if (playerX < 500 && this._lastHintZone !== "intro") {
          this._lastHintZone = "intro";
          bridge.emit(
            "hint",
            "Welcome to Resume Kingdom! Tap left/right to move, middle to jump"
          );
        } else if (
          playerX >= 1500 &&
          playerX < 2200 &&
          this._lastHintZone !== "about"
        ) {
          this._lastHintZone = "about";
          bridge.emit("hint", "📖 About Me section - Learn about the king!");
        } else if (
          playerX >= 2400 &&
          playerX < 3200 &&
          this._lastHintZone !== "skills"
        ) {
          this._lastHintZone = "skills";
          bridge.emit("hint", "🛡️ Skills Arsenal - Master your abilities!");
        } else if (
          playerX >= 3400 &&
          playerX < 4000 &&
          this._lastHintZone !== "projects"
        ) {
          this._lastHintZone = "projects";
          bridge.emit("hint", "🚀 Project Battles zone - See my conquests!");
        } else if (
          playerX >= 4000 &&
          playerX < 4800 &&
          this._lastHintZone !== "experience"
        ) {
          this._lastHintZone = "experience";
          bridge.emit("hint", "💼 Experience realm - Journey through work!");
        } else if (
          playerX >= 5000 &&
          playerX < 5700 &&
          this._lastHintZone !== "education"
        ) {
          this._lastHintZone = "education";
          bridge.emit("hint", "🎓 Education realm - Academic milestones!");
        } else if (playerX >= 6700 && this._lastHintZone !== "hire") {
          this._lastHintZone = "hire";
          bridge.emit(
            "hint",
            "👑 Final destination - Click the buttons to hire the king!"
          );
        }
      }
    }

    const config = {
      type: Phaser.AUTO,
      parent: containerRef.current,
      width: WIDTH,
      height: HEIGHT,
      backgroundColor: "#87CEEB",
      physics: {
        default: "arcade",
        arcade: { gravity: { y: 900 }, debug: false },
      },
      scene: [KingdomScene],
      scale: {
        mode: Phaser.Scale.RESIZE,
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
      fps: { target: 60 },
      render: { antialias: true, pixelArt: false },
    };

    gameRef.current = new Phaser.Game(config);

    const onResize = () => {
      if (gameRef.current && gameRef.current.scene.scenes[0]) {
        bridge.emit(
          "hint",
          "Tap left/right sides to move, middle to jump, or scroll/swipe to explore"
        );
      }
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      if (gameRef.current) {
        gameRef.current.destroy(true);
        gameRef.current = null;
      }
    };
  }, []);

  return (
    <div className="relative w-full h-[80vh] min-h-[600px] bg-gradient-to-b from-sky-400 to-sky-200 rounded-2xl overflow-hidden shadow-2xl">
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-sky-900/95 z-50">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-4 border-yellow-400 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-yellow-200 text-lg">Loading the Kingdom...</p>
          </div>
        </div>
      )}

      {/* Phaser mounts here */}
      <div ref={containerRef} className="absolute inset-0" />

      {/* Level indicator */}
      <div className="pointer-events-none absolute top-4 right-4 rounded-full bg-gradient-to-r from-yellow-400 to-orange-400 px-4 py-2 text-gray-900 font-bold shadow-xl">
        👑 LEVEL {level}
      </div>

      {/* Checkpoint indicator */}
      <div className="pointer-events-none absolute top-4 left-4 rounded-lg bg-black/60 backdrop-blur-md px-4 py-2 text-yellow-300 text-sm font-medium shadow-lg">
        🗺️ {checkpoint.toUpperCase()}
      </div>

      {/* Mobile scroll hint */}
      <div className="pointer-events-none absolute top-1/2 right-2 transform -translate-y-1/2 text-yellow-300/70 text-xs block sm:hidden">
        <div className="flex flex-col items-center space-y-2">
          <div className="animate-bounce">👆</div>
          <div className="rotate-90 text-center">Swipe</div>
        </div>
      </div>

      {/* Desktop scroll hint */}
      <div className="pointer-events-none absolute top-1/2 right-4 transform -translate-y-1/2 text-yellow-300/70 text-xs hidden sm:block">
        <div className="flex flex-col items-center space-y-2">
          <div className="animate-bounce">🖱️</div>
          <div className="text-center">Scroll</div>
        </div>
      </div>

      {/* Bottom hint bar (uses `hint` state) */}
      <div className="pointer-events-none absolute bottom-2 left-0 right-0 flex justify-center px-4">
        <div className="bg-black/60 backdrop-blur-md text-yellow-200 text-xs sm:text-sm px-4 py-2 rounded-full shadow-lg max-w-[90%] text-center">
          {hint}
        </div>
      </div>
    </div>
  );
}

